import { coreService } from './core-service'
import { featuredGuestJourneyService } from './featured-guest-journey-service'
import { config } from '../util/config'
import { EventBusMessage } from '../util/event-bus-util'
import { logger } from '../util/logger'
import mixpanel from 'mixpanel-browser'

const PLATFORM = 'web'

/** Shared init options, so the guest and authed paths cannot drift on residency or DNT. */
const initOptions = {
  // Route ingestion to the EU residency endpoint — US forwarding ends July 2026 (GH-784)
  api_host: 'https://api-eu.mixpanel.com',
  debug: config.mixpanel.debug,
  ignore_dnt: true,
}

let initialised = false

// TODO: Move to react-common, we have the same in mobile app (removed programmes part since we don't have them yet)
export enum MixpanelEvent {
  WORKOUT_STARTED = 'Workout Started',
  WORKOUT_COMPLETED = 'Workout Completed',
  PAYWALL_OPENED = 'Paywall Opened',
  CHECKOUT_INITIATED = 'Checkout Initiated',
  SUBSCRIPTION_SET = 'Subscription Set',
  APP_TOUR_STARTED = 'app_tour_started',
  APP_TOUR_COMPLETED = 'app_tour_completed',
  APP_TOUR_DISMISSED = 'app_tour_dismissed',

  // Featured (free) workouts — GH-867. Deliberately distinct from WORKOUT_STARTED /
  // WORKOUT_COMPLETED and from PAYWALL_OPENED, so free-workout engagement and upsell
  // conversion can each be counted without inferring them from a workout id.
  FEATURED_SHELF_VIEWED = 'Featured Shelf Viewed',
  FEATURED_WORKOUT_PLAY_CLICKED = 'Featured Workout Play Clicked',
  FEATURED_WORKOUT_PREVIEW_STARTED = 'Featured Workout Preview Started',
  FEATURED_WORKOUT_STARTED = 'Featured Workout Started',
  FEATURED_WORKOUT_COMPLETED = 'Featured Workout Completed',
  FEATURED_WORKOUT_ENDED = 'Featured Workout Ended',
  FEATURED_WORKOUT_PLAY_FAILED = 'Featured Workout Play Failed',
  /**
   * The post-workout prompt. With the paywall auto-opening after the completion modal there
   * is no click to record, so this single event is both the impression and the open;
   * conversion is read off the paywall via `PaywallContext.source`.
   */
  FEATURED_UPSELL_SHOWN = 'Featured Upsell Shown',
}

/** Where a featured card was played from — the two surfaces convert very differently. */
export type FeaturedSurface = 'home' | 'sign-in'

export const mixpanelService = {
  /**
   * Guest init — for the signed-out sign-in page, which renders outside `SessionContext`.
   *
   * `init()` below cannot be reused there: it calls `coreService.getMe()` on the authed client
   * and identifies with the hustler id, neither of which exists for a guest.
   *
   * It deliberately does **not** call `identify`. Mixpanel already assigns an anonymous
   * `$device_id` on init, and that is exactly the right identity for a visitor with no account
   * — it is what makes "unique visitors who played a free workout" countable. Identifying a
   * guest as anything would turn an anonymous visitor into a named profile that never receives
   * people properties, for no gain. We mint no guest id of our own either: the repo already
   * has `clientGuidService` for a per-browser correlator, and a third identifier for analytics
   * would be a duplicate concept holding the same non-information.
   *
   * Safe to call repeatedly; only the first call initialises.
   */
  initForGuest: (): void => {
    try {
      if (!initialised) {
        mixpanel.init(config.mixpanel.projectToken, initOptions)
        initialised = true
      }
    } catch (err) {
      // Analytics must never take the sign-in page down with it.
      logger.debug('guest mixpanel init failed', err)
    }
  },

  /**
   * A guest started a free workout. Tracked *and* remembered — the memory is what lets the
   * account they may create carry "trained before signing up".
   */
  featuredGuestPlayStarted: ({ workoutId, workoutTitle }: { workoutId: string; workoutTitle: string }): void => {
    mixpanelService.trackFeatured(MixpanelEvent.FEATURED_WORKOUT_STARTED, {
      workoutId,
      workoutTitle,
      surface: 'sign-in',
      signedIn: false,
    })
    try {
      featuredGuestJourneyService.recordPlay({ workoutId })
    } catch (err) {
      logger.debug('featured guest play not recorded', err)
    }
  },

  /** A guest passed the completion threshold. Same deal. */
  featuredGuestPlayCompleted: ({ workoutId, workoutTitle }: { workoutId: string; workoutTitle: string }): void => {
    mixpanelService.trackFeatured(MixpanelEvent.FEATURED_WORKOUT_COMPLETED, {
      workoutId,
      workoutTitle,
      surface: 'sign-in',
      signedIn: false,
    })
    try {
      featuredGuestJourneyService.recordCompletion()
    } catch (err) {
      logger.debug('featured guest completion not recorded', err)
    }
  },

  init: async (): Promise<void> => {
    mixpanel.init(config.mixpanel.projectToken, initOptions)
    initialised = true
    const myInfo = await coreService.getMe()

    mixpanel.identify(myInfo.id)

    /**
     * Carry the guest journey onto the account. **This is the whole of the guest→account join**
     * — there is deliberately no `alias` and no `$merge`; see
     * `featured-guest-journey-service.ts` for why those were rejected.
     *
     * `set_once`, so a later guest session on a shared browser cannot revise the history of an
     * account that already has one. Then cleared, so the *next* account signed in on this
     * browser does not inherit a journey that was not theirs.
     */
    try {
      const featuredGuestProperties = featuredGuestJourneyService.toProperties()
      if (featuredGuestProperties) {
        mixpanel.people.set_once(featuredGuestProperties)
        featuredGuestJourneyService.clear()
      }
    } catch (err) {
      logger.debug('featured guest journey not stamped', err)
    }

    mixpanel.people.set({ email: myInfo.email })
    mixpanel.people.set({ gender: myInfo.profile.gender })
    mixpanel.people.set({ dateOfBirth: myInfo.profile.dateOfBirth })
    mixpanel.people.set({ username: myInfo.profile.username })
    mixpanel.people.set({ $email: myInfo.email })
  },
  paywallOpened: (eventMessage: { message: EventBusMessage }): void => {
    mixpanel.track(MixpanelEvent.PAYWALL_OPENED, eventMessage)
  },

  sendCheckoutInitiated: (eventMessage: { message: EventBusMessage }): void => {
    mixpanel.track(MixpanelEvent.CHECKOUT_INITIATED, eventMessage)
  },

  sendSubscriptionSet: (eventMessage: { message: EventBusMessage }): void => {
    mixpanel.track(MixpanelEvent.SUBSCRIPTION_SET, eventMessage)
  },

  sendVideoPlaybackStarted: (eventMessage: { message: EventBusMessage }): void => {
    mixpanel.track(MixpanelEvent.WORKOUT_STARTED, eventMessage)
  },

  sendVideoPlaybackEnded: (eventMessage: { message: EventBusMessage }): void => {
    mixpanel.track(MixpanelEvent.WORKOUT_COMPLETED, eventMessage)
  },
  trackAppTourStarted: (): void => {
    mixpanel.track(MixpanelEvent.APP_TOUR_STARTED)
  },
  trackAppTourCompleted: (): void => {
    mixpanel.track(MixpanelEvent.APP_TOUR_COMPLETED)
  },
  trackAppTourDismissed: (step: number): void => {
    mixpanel.track(MixpanelEvent.APP_TOUR_DISMISSED, { step })
  },

  /**
   * Featured-workout tracking. Every one of these is wrapped: on the sign-in page these fire
   * before the app has bootstrapped anything, and a Mixpanel failure must not surface to a
   * visitor who is only trying to watch a workout.
   */
  trackFeatured: (event: MixpanelEvent, properties: Record<string, unknown> = {}): void => {
    try {
      mixpanel.track(event, {
        platform: PLATFORM,
        ...properties,
      })
    } catch (err) {
      logger.debug('featured workout event not tracked', err)
    }
  },
}
